import { ApiError, csrf, type NodeInfo, type NodeRef } from '../../api'
import { noteServerTimeRest, toCenter } from './clock'

/*
 * Routing network, R0 read-only (docs/controlapi.yaml, tag relay;
 * docs/architecture/routing-network/backend/04-admin-api.md). Only what the
 * R0 screens read: routes, hops, epochs, series, traces, the journal, links,
 * the relay fields of a node, settings and the SSE events. Fields that R0.9h
 * adds (route state no_path, source_grace_until, NodeInfo.provider) are
 * optional: older centers do not send them.
 */

export const RELAY = '/api/admin/relay'
export const RELAY_EVENTS_PATH = `${RELAY}/events`

/** Older than this (now − as_of) the data is stale (04-admin-api.md §2.2). */
export const STALE_MS = 15_000
/** Polling period while the SSE stream is lost. */
export const POLL_MS = 5_000

/** integrations: an integrations node (platform APIs and the chat shard, no media); always alone. */
export type RelayRole = 'ingress' | 'relay' | 'egress' | 'hub' | 'integrations'

/** NodeRelay.state: the first that applies (05-state-machines.md §5). */
export type NodeRelayState = 'revoked' | 'disabled' | 'unsupported' | 'offline' | 'unregistered' | 'registered' | 'in_peers' | 'ready'

export type NoPathReason = 'no_hub' | 'capacity' | 'ingress_unsupported' | 'relay_disabled' | 'ingress_not_ready'

/** A stream on a via_center node without a relay route (kept in the center's memory). */
export interface NoPathStream {
  stream: string
  ingress_node_id: string
  /** the stream's owner (R0.9h) */
  user_id?: number
  /** API-UI: the owner with the e-mail, so the console can name and filter it */
  user?: { id: number; email?: string }
  reason: NoPathReason
  since: string
}

/** The owner of a stream without a route: user.id, else user_id. */
export const noPathUser = (x: NoPathStream): number | undefined => x.user?.id ?? x.user_id

/**
 * What a network action takes from live routes (ConfirmError.impact, R0.9g;
 * revoke with SEC-CENTER, decision 48). Every refusal of the center carries it,
 * so the console shows it before the action.
 */
export interface NodeImpact {
  /** what the patch takes from the node (absent for a revoke: everything) */
  takes: ('relay' | 'egress' | 'hub')[]
  /** live legs of the node on the routes touched (a transit counts two) */
  legs: number
  routes: number
  streams: number
  /** the routes touched, at most 50 */
  route_ids?: string[]
}

export interface PeersRejected {
  version: number
  reason: string
  source: 'event' | 'ack'
  at: string
}

export interface NodeRelay {
  enabled: boolean
  state: NodeRelayState
  proto?: number
  caps?: string[]
  listen: boolean
  versions?: number[]
  /** SHA-256 of the SubjectPublicKeyInfo of the relay key, 64 hex */
  spki?: string
  /** the next key during a rotation */
  spki_next?: string
  spki_changed_at?: string
  endpoints: string[]
  peers_version: number
  peers_version_acked: number
  in_peers: boolean
  /** R0.9i: the other nodes of the current peer list that trust this node's current key */
  peers_acked?: number
  /** R0.9i: the other nodes of the current peer list (online or not) */
  peers_total?: number
  peers_rejected?: PeersRejected
  /** API-UI: ids of the other nodes that have not acknowledged a peers list with this node's current key yet (≤ 50) */
  peers_pending?: string[]
  /** PROV-RELAY-1: of peers_pending, those not connected (≤ 50); one that never comes back holds every node's trust short until revoked */
  peers_pending_offline?: string[]
  /** API-UI: live relay legs of the node (not down), from relay reports and the controller, without host/1 */
  legs?: number
  /** API-UI: of them up */
  legs_up?: number
  accept_transit: boolean
  routes_version: number
  routes_version_acked: number
  no_path?: NoPathStream[]
}

export interface NodeLoad {
  cpus: number
  cpu_pct: number
  mem_pct: number
  load1: number
  rx_bps: number
  tx_bps: number
  streams_ingress: number
  streams_egress: number
  streams_transit: number
  legs: number
  legs_up: number
}

export interface NodeClock {
  offset_ms: number
  rtt_ms?: number
  source: 'ping' | 'hello'
  synced?: boolean
  est_error_us?: number
  suspect: boolean
}

/** The relay part of a node (SSE node_relay, RelaySnapshot.nodes). */
export interface RelayNodeEvent {
  id: string
  status: 'online' | 'offline' | 'revoked'
  relay?: NodeRelay
  load?: NodeLoad
  clock?: NodeClock
}

export type LinkState = 'unknown' | 'up' | 'degraded' | 'down' | 'idle'

export interface RelayLink {
  link_id: string
  from: NodeRef
  to: NodeRef
  state: LinkState
  state_reason: '' | 'dial_failed' | 'tls_failed' | 'peer_rejected' | 'idle_timeout' | 'high_rtt' | 'retransmits' | 'resuming' | 'node_offline'
  legs: number
  legs_up: number
  rtt_p50_ms?: number
  rtt_p99_ms?: number
  lat_p50_ms?: number
  lat_p99_ms?: number
  retrans_pct?: number
  loss_pct?: number
  util_bps: number
  capacity_bps: number | null
  routes: number
  route_ids: string[]
  since: string
  last_failure?: { at: string; reason: string }
}

export type RouteState = 'pending' | 'active' | 'degraded' | 'switching' | 'on_standby' | 'no_path' | 'ended'
export type HopState = 'connecting' | 'handshake' | 'offered' | 'up' | 'resuming' | 'down'

export interface RelayHop {
  index: number
  from: NodeRef
  to: NodeRef
  state: HopState
  dialer?: 'up' | 'down'
  lat_p50_ms?: number
  lat_p99_ms?: number
  rtt_ms?: number
  retrans_pct?: number
  since?: string
}

export interface RouteCounters {
  gaps: number
  gap_frames: number
  dups: number
  reorder: number
  epoch_drops: number
  resumes: number
}

export interface RelayRoute {
  route_id: string
  stream: string
  user?: { id: number; email?: string }
  path: NodeRef[]
  hops: RelayHop[]
  epoch: number
  mode: 'auto' | 'pinned'
  pinned: { by?: string; at?: string } | null
  state: RouteState
  /** '' or a controller code; an ended route has its end reason */
  state_reason: string
  state_since: string
  source?: { live: boolean; since?: string }
  e2e_p50_ms?: number
  e2e_p99_ms?: number
  jitter_ms: number
  counters: RouteCounters
  last_resume: { at?: string; result?: 'ring' | 'gap' | 'fresh'; gap_frames?: number; outage_ms?: number } | null
  outputs: { total: number; by_state: Record<string, number> }
  standbys: unknown[]
  active_switch_id: string | null
  changed_by: 'controller' | 'operator' | 'failover'
  changed_at: string
  created_at: string
  ended_at?: string
  end_reason?: string
  /** R0.9h: until when the outputs hold the platform sessions without a source */
  source_grace_until?: string
  /** API-UI: the hold in force for the route's stream (its key's, else the center's default), ms */
  source_grace_ms?: number
}

export interface RelayJournalEvent {
  id: string
  t: string
  kind: string
  node_id: string
  peer_node_id?: string
  route_id?: string
  epoch?: number
  stream?: string
  details?: Record<string, unknown>
}

export interface RelayRouteDetail extends RelayRoute {
  as_of: string
  recent_events: RelayJournalEvent[]
}

export interface RelayEpoch {
  epoch: number
  kind: 'primary' | 'standby'
  standby_rank?: number
  state: 'planned' | 'active' | 'retired' | 'superseded'
  path: NodeRef[]
  origin: 'controller' | 'operator' | 'failover'
  actor: string | null
  switch_id: string | null
  cutover_seq: number | null
  activated_at?: string
  retired_at?: string
}

export type StatsRange = '15m' | '1h' | '6h' | '24h' | '7d' | '30d'
export type StatsStep = '5s' | '1m' | '1h'

export interface RelayStatsPoint {
  t: string
  samples?: number
  lat_p50_ms?: number
  lat_p99_ms?: number
  lat_max_ms?: number
  e2e_p50_ms?: number
  e2e_p99_ms?: number
  rtt_ms?: number
  retrans_pct?: number
  bps: number
  frames?: number
  gaps?: number
  gap_frames?: number
  dups?: number
  reorder?: number
  epoch_drops?: number
  resumes?: number
  outage_max_ms?: number
  clock_suspect?: boolean
  /** links only */
  failures?: number
  legs?: number
  legs_up?: number
}

/** GET /api/admin/nodes/{id}/metrics: one point of a node's load. */
export interface LoadPoint {
  t: string
  cpu_pct?: number
  mem_pct?: number
  load1?: number
  rx_bps?: number
  tx_bps?: number
  legs_up?: number
  /** API-UI: relay legs (not down), next to legs_up */
  legs?: number
  clock_offset_ms?: number
}

export interface NodeMetrics {
  as_of: string
  node_id: string
  step_s: 5 | 60 | 900 | 3600
  points: LoadPoint[]
}

/** GET / PATCH /api/admin/nodes/{id}. */
export type NodeInfoAsOf = NodeInfo & { as_of: string }

export interface RelayStatsSeries {
  as_of: string
  step: StatsStep
  series: { hop?: number; epoch?: number; from: string; to: string; points: RelayStatsPoint[] }[]
}

/** Trace stage numbers (r0-design §13). */
export const TRACE_STAGE: Record<number, string> = { 1: 'ring write', 2: 'socket send', 3: 'receive', 4: 'bus write', 5: 'platform write' }

export interface RelayTraceSample {
  t: string
  route_id: string
  epoch: number
  seq: number
  instance_id: string
  reason: 'sampled' | 'gap' | 'resume' | 'clock_suspect' | 'cutover' | 'failover' | 'slow'
  clock_suspect: boolean
  stages: { node: string; stage: number; unix_ns: number }[]
  outputs?: { output_id: string; unix_ns: number }[]
}

export interface RelaySettings {
  as_of: string
  enabled: boolean
  defaults: {
    source_grace_ms: number
    resume_window_ms: number
    ring_ms: number
    ping_ms: number
    idle_timeout_ms: number
    trace_every_ms: number
    max_legs_per_node: number
    jitter_ms: number
  }
  retention: { traces_days: number; series_5s_days: number; series_1m_days: number; series_1h_days: number; events_days: number }
  second_factor: 'provisioner' | 'inline'
}

export interface RelaySnapshot {
  as_of: string
  /** the center's clock when the event went out (decision 37) */
  server_time?: string
  routes: RelayRoute[]
  links: RelayLink[]
  nodes: RelayNodeEvent[]
  no_path?: NoPathStream[]
}

export interface RelayList<T> {
  as_of: string
  items: T[]
  next_cursor: string | null
}

/** RelayError.code (04-admin-api.md §2.1); R0 answers bad_request, range_too_large and not_found. */
export type RelayErrorCode = 'bad_request' | 'not_found' | 'range_too_large' | (string & {})

/** The SSE events of R0 (the description of /api/admin/relay/events). */
export const RELAY_EVENT_NAMES = ['snapshot', 'route', 'route_removed', 'link', 'link_removed', 'node_relay', 'journal', 'no_path', 'no_path_removed'] as const
export type RelayEventName = (typeof RELAY_EVENT_NAMES)[number]

/** A relay answer with the time it was true (as_of / X-As-Of) and when it arrived, both on the center's clock. */
export interface AsOf<T> {
  data: T
  /** center time the data was true, ms since the epoch */
  asOf: number
  /** center time the answer arrived (browser time + the clock offset of decision 37) */
  at: number
}

/**
 * A request to the relay API, /api/admin/nodes* or /api/admin/outputs*: data plus
 * as_of from the body or X-As-Of; X-Server-Time feeds the clock offset.
 */
export async function requestAsOf<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<AsOf<T>> {
  const sent = Date.now()
  const method = init.method ?? 'GET'
  const headers: Record<string, string> = {}
  // controlapi refuses a non-GET without the session's CSRF token (403), like api() sends it
  if (method !== 'GET') headers['X-CSRF-Token'] = csrf()
  if (init.body !== undefined) headers['Content-Type'] = 'application/json'
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers,
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  })
  const arrived = Date.now()
  noteServerTimeRest(res.headers.get('X-Server-Time'), sent, arrived)
  const text = await res.text()
  let data: unknown
  try {
    data = text ? JSON.parse(text) : undefined
  } catch {
    // not JSON (proxy error page)
  }
  if (!res.ok) {
    const msg = (data as { error?: string } | undefined)?.error ?? `Request failed (${res.status})`
    throw new ApiError(res.status, msg, data)
  }
  const at = toCenter(arrived)
  const raw = (data as { as_of?: string } | undefined)?.as_of ?? res.headers.get('X-As-Of') ?? undefined
  const asOf = raw ? Date.parse(raw) : NaN
  return { data: data as T, asOf: Number.isFinite(asOf) ? asOf : at, at }
}

/** GET of a relay endpoint (or GET /api/admin/nodes): data plus as_of from the body or X-As-Of. */
export const getAsOf = <T,>(path: string) => requestAsOf<T>(path)

/** Query keys of the relay data; the SSE client invalidates the ['relay'] prefix after a reconnect. */
export const relayKeys = {
  settings: ['relay', 'settings'] as const,
  routes: ['relay', 'routes'] as const,
  routesList: ['relay', 'routes', 'list'] as const,
  links: ['relay', 'links'] as const,
  route: (id: string) => ['relay', 'route', id] as const,
  epochs: (id: string) => ['relay', 'route', id, 'epochs'] as const,
  stats: (id: string, range: StatsRange) => ['relay', 'route', id, 'stats', range] as const,
  traces: (id: string) => ['relay', 'route', id, 'traces'] as const,
  journal: (id: string) => ['relay', 'route', id, 'journal'] as const,
  outputs: (stream: string) => ['relay', 'outputs', stream] as const,
  nodes: ['relay', 'nodes'] as const,
  node: (id: string) => ['relay', 'node', id] as const,
  nodeMetrics: (id: string, range: StatsRange) => ['relay', 'node', id, 'metrics', range] as const,
  nodeRoutes: (id: string) => ['relay', 'node', id, 'routes'] as const,
  nodeLinks: (id: string) => ['relay', 'node', id, 'links'] as const,
  nodeJournal: (id: string) => ['relay', 'node', id, 'journal'] as const,
  link: (id: string) => ['relay', 'link', id] as const,
  linkStats: (id: string, range: StatsRange) => ['relay', 'link', id, 'stats', range] as const,
}

export const routePath = (id: string) => `/admin/network/routes/${encodeURIComponent(id)}`

export type NodeTab = 'overview' | 'streams' | 'links' | 'events' | 'key' | 'host' | 'bans'

/** The node card (Network › Nodes › node), optionally on a tab and with a link open. */
export function nodePath(id: string, tab?: NodeTab, q: Record<string, string> = {}) {
  const p = new URLSearchParams(q)
  if (tab && tab !== 'overview') p.set('tab', tab)
  const s = p.toString()
  return `/admin/network/nodes/${encodeURIComponent(id)}${s ? `?${s}` : ''}`
}

export const NODES_PATH = '/admin/network/nodes'
/** Network › Routes (the list; R0.13c). */
export const ROUTES_PATH = '/admin/network/routes'
/** Network › Map: in R0 only the Table view (links between nodes). */
export const MAP_PATH = '/admin/network/map'
/** Network › Captions: the speech recognition workers and their jobs (owner 21.10) */
export const CAPTIONS_PATH = '/admin/network/captions'
export const TRANSCODERS_PATH = '/admin/network/transcoders'
export const mapTablePath = (q: Record<string, string> = {}) => `${MAP_PATH}?${new URLSearchParams({ view: 'table', ...q })}`

/** GET /api/admin/relay/routes: items plus, on the first page, the streams with no route at all. */
export interface RelayRouteList extends RelayList<RelayRoute> {
  no_path?: NoPathStream[]
}
